/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { Form, Alert } from 'antd';
import localforage from 'localforage';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import { FiArrowLeft } from 'react-icons/fi';

import { useAppDispatch } from '@/hooks/useRedux';
import {
  postSignInIntroduction,
  postSignInIdentification,
  postSignInConfirmation,
} from '@/redux/auth/signin/thunk';

import Introduction from './signinSteps/Introduction';
import Identification from './signinSteps/Identification';
import OTP from './signinSteps/OTP';
import Logo from '@/components/layout/Logo';
import ThemedButton from '@/components/common/buttons/Button';
import AuthFooter from '../AuthFooter';
import { getProfessionalErrorMessage, extractErrorMessage } from '@/utils/errorHandling';
import { getRuntimeOrg } from '@/utils/runtimeOrg';
import { bometConfig } from '@/config/bomet.config';
import { nyandaruaConfig } from '@/config/nyandarua.config';
import { baringoConfig } from '@/config/baringo.config';
import { limsConfig } from '@/config/lims.config';

const ORG_CONFIG_MAP: Record<string, any> = {
  bomet: bometConfig,
  nyandarua: nyandaruaConfig,
  baringo: baringoConfig,
  lims: limsConfig,
};

const DEFAULT_SIGNIN_WELCOME = 'Welcome Back!';
const DEFAULT_SIGNIN_TAGLINE =
  'Log In To Manage County Services, Make Payments, And Track Transactions Easily.';
const DEFAULT_SIGNIN_HERO_IMAGE = '/images/auth-hero.svg';

function resolveSigninCopy() {
  if (typeof document === 'undefined') {
    return {
      welcome: DEFAULT_SIGNIN_WELCOME,
      tagline: DEFAULT_SIGNIN_TAGLINE,
      heroImage: DEFAULT_SIGNIN_HERO_IMAGE,
    };
  }
  const htmlOrg = document.documentElement.dataset.org;
  const org = (htmlOrg || getRuntimeOrg() || '').toLowerCase();
  const cfg = ORG_CONFIG_MAP[org];
  return {
    welcome: cfg?.auth?.signin?.welcome ?? DEFAULT_SIGNIN_WELCOME,
    tagline: cfg?.auth?.signin?.tagline ?? DEFAULT_SIGNIN_TAGLINE,
    heroImage: cfg?.auth?.signin?.heroImage ?? DEFAULT_SIGNIN_HERO_IMAGE,
  };
}

type Step1Data = { session_id?: string | null };
type IdPayload = { id_type: string; value: string; password: string };

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

const GridBackground = () => (
  <svg
    className="pointer-events-none absolute inset-0 h-full w-full"
    width="100%"
    height="100%"
    viewBox="0 0 750 560"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_35_26103"
      style={{ maskType: 'alpha' }}
      maskUnits="userSpaceOnUse"
      x={-9}
      y={-208}
      width={768}
      height={768}
    >
      <rect
        width="768"
        height="768"
        transform="translate(-9 -208)"
        fill="url(#paint0_radial_35_26103)"
      />
    </mask>
    <g mask="url(#mask0_35_26103)">
      <g clipPath="url(#clip0_35_26103)">
        <g clipPath="url(#clip1_35_26103)">
          <line x1="39.5" y1="-208" x2="39.5" y2="560" stroke="#E9EAEB" />
          <line x1="87.5" y1="-208" x2="87.5" y2="560" stroke="#E9EAEB" />
          <line x1="135.5" y1="-208" x2="135.5" y2="560" stroke="#E9EAEB" />
          <line x1="183.5" y1="-208" x2="183.5" y2="560" stroke="#E9EAEB" />
          <line x1="231.5" y1="-208" x2="231.5" y2="560" stroke="#E9EAEB" />
          <line x1="279.5" y1="-208" x2="279.5" y2="560" stroke="#E9EAEB" />
          <line x1="327.5" y1="-208" x2="327.5" y2="560" stroke="#E9EAEB" />
          <line x1="375.5" y1="-208" x2="375.5" y2="560" stroke="#E9EAEB" />
          <line x1="423.5" y1="-208" x2="423.5" y2="560" stroke="#E9EAEB" />
          <line x1="471.5" y1="-208" x2="471.5" y2="560" stroke="#E9EAEB" />
          <line x1="519.5" y1="-208" x2="519.5" y2="560" stroke="#E9EAEB" />
          <line x1="567.5" y1="-208" x2="567.5" y2="560" stroke="#E9EAEB" />
          <line x1="615.5" y1="-208" x2="615.5" y2="560" stroke="#E9EAEB" />
          <line x1="663.5" y1="-208" x2="663.5" y2="560" stroke="#E9EAEB" />
          <line x1="711.5" y1="-208" x2="711.5" y2="560" stroke="#E9EAEB" />
        </g>
        <rect x="-8.5" y="-207.5" width="767" height="767" stroke="#E9EAEB" />
        <g clipPath="url(#clip2_35_26103)">
          <line x1="-9" y1="31.5" x2="759" y2="31.5" stroke="#E9EAEB" />
          <line x1="-9" y1="79.5" x2="759" y2="79.5" stroke="#E9EAEB" />
          <line x1="-9" y1="127.5" x2="759" y2="127.5" stroke="#E9EAEB" />
          <line x1="-9" y1="175.5" x2="759" y2="175.5" stroke="#E9EAEB" />
          <line x1="-9" y1="223.5" x2="759" y2="223.5" stroke="#E9EAEB" />
          <line x1="-9" y1="271.5" x2="759" y2="271.5" stroke="#E9EAEB" />
          <line x1="-9" y1="319.5" x2="759" y2="319.5" stroke="#E9EAEB" />
          <line x1="-9" y1="367.5" x2="759" y2="367.5" stroke="#E9EAEB" />
          <line x1="-9" y1="415.5" x2="759" y2="415.5" stroke="#E9EAEB" />
          <line x1="-9" y1="463.5" x2="759" y2="463.5" stroke="#E9EAEB" />
          <line x1="-9" y1="511.5" x2="759" y2="511.5" stroke="#E9EAEB" />
          <line x1="-9" y1="559.5" x2="759" y2="559.5" stroke="#E9EAEB" />
        </g>
        <rect x="-8.5" y="-207.5" width="767" height="767" stroke="#E9EAEB" />
      </g>
    </g>
    <defs>
      <radialGradient
        id="paint0_radial_35_26103"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(384 384) rotate(90) scale(384 384)"
      >
        <stop />
        <stop offset="1" stopOpacity="0" />
      </radialGradient>
      <clipPath id="clip0_35_26103">
        <rect
          width="768"
          height="768"
          fill="white"
          transform="translate(-9 -208)"
        />
      </clipPath>
      <clipPath id="clip1_35_26103">
        <rect x="-9" y="-208" width="768" height="768" fill="white" />
      </clipPath>
      <clipPath id="clip2_35_26103">
        <rect x="-9" y="-208" width="768" height="768" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const SigninPage = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const router = useRouter();

  const [step1Data, setStep1Data] = useState<Step1Data | null>(null);
  const [idPayload, setIdPayload] = useState<IdPayload | null>(null);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  // Single lock across all steps — prevents double-submit on slow connections.
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Org-aware sign-in copy (falls back to the default county-services tagline).
  const [signinCopy, setSigninCopy] = useState({
    welcome: DEFAULT_SIGNIN_WELCOME,
    tagline: DEFAULT_SIGNIN_TAGLINE,
    heroImage: DEFAULT_SIGNIN_HERO_IMAGE,
  });
  useEffect(() => {
    setSigninCopy(resolveSigninCopy());
  }, []);

  const isSafeRel = (p?: string | null) => !!p && /^\/(?!\/)/.test(p);

  const getNextFromURL = (): string | null => {
    if (typeof window === 'undefined') return null;
    const next = new URLSearchParams(window.location.search).get('next');
    return isSafeRel(next) ? next! : null;
  };

  const getNextFromSession = (): string | null => {
    try {
      if (typeof window === 'undefined') return null;
      const next = sessionStorage.getItem('postLoginRedirect');
      return isSafeRel(next) ? next! : null;
    } catch {
      return null;
    }
  };

  const pickNextDestination = (): string =>
    getNextFromURL() || getNextFromSession() || '/account';

  const clearStashedNext = () => {
    try {
      sessionStorage.removeItem('postLoginRedirect');
      sessionStorage.removeItem('postLoginRedirectAbs');
    } catch {
      /* ignore */
    }
  };

  // Wipe any previous signin artifacts so we always start clean
  useEffect(() => {
    (async () => {
      try {
        await Promise.all([
          localforage.removeItem('signin.session'),
          localforage.removeItem('signin.step'),
          localforage.removeItem('signin.id_payload'),
        ]);
      } catch {
        /* ignore */
      }

      setStep1Data(null);
      setIdPayload(null);
      setActiveStep(0);
      setInlineError(null);
      form.resetFields();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefetch the post-login destination so the redirect feels instant
  useEffect(() => {
    const dest = pickNextDestination();
    (router as any)?.prefetch?.(dest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When arriving at the OTP step, sync hidden form fields from in-memory state
  // (or localforage if the page was refreshed mid-flow)
  useEffect(() => {
    (async () => {
      if (activeStep !== 2) return;

      let sessionId = step1Data?.session_id ?? null;
      if (!sessionId) {
        const cached = await localforage.getItem<Step1Data>('signin.session');
        sessionId = cached?.session_id ?? null;
      }

      let payload = idPayload;
      if (!payload) {
        payload =
          (await localforage.getItem<IdPayload>('signin.id_payload')) || null;
      }

      form.setFieldsValue({
        session_id: sessionId ?? undefined,
        id_type: payload?.id_type ?? undefined,
        value: payload?.value ?? undefined,
        password: payload?.password ?? undefined,
      });
    })();
  }, [activeStep, form, idPayload, step1Data]);

  const stepMeta = useMemo(
    () => [
      {
        title: 'Step 1: Introduction',
        description:
          'Tell us your nationality and whether you are an alien or a refugee.',
      },
      {
        title: 'Step 2: Identification',
        description:
          'Enter your identification document type, the document value, and your account password.',
      },
      {
        title: 'Step 3: OTP Confirmation',
        description:
          'Enter the one-time code sent to your registered contact to complete sign in.',
      },
    ],
    [],
  );

  const currentMeta = stepMeta[activeStep] ?? stepMeta[0];

  const handleIntroNext = async () => {
    setInlineError(null);

    const values = await form.validateFields([
      'nationality',
      'is_alien',
      'is_refugee',
    ]);

    const res = await dispatch(
      postSignInIntroduction({
        nationality: values.nationality ?? 'KE',
        is_alien: !!values.is_alien,
        is_refugee: !!values.is_refugee,
      }),
    ).unwrap();

    const cache: Step1Data = { session_id: res?.session_id ?? null };
    await localforage.setItem('signin.session', cache);
    await localforage.setItem('signin.step', 1);

    setStep1Data(cache);
    setActiveStep(1);
  };

  const handleIdentificationNext = async () => {
    setInlineError(null);

    const values = await form.validateFields(['id_type', 'value', 'password']);

    // Prefer in-memory; fall back to cache if the page was refreshed
    let sessionId = step1Data?.session_id ?? null;
    if (!sessionId) {
      const cached = await localforage.getItem<Step1Data>('signin.session');
      sessionId = cached?.session_id ?? null;
    }

    if (!sessionId) {
      setInlineError('Session expired or missing. Please restart sign in.');
      return;
    }

    const id_type = values.id_type || 'fc09df21-5606-4c8a-a921-199bd7b0ce64';
    const value = values.value;
    const password = values.password;

    await dispatch(
      postSignInIdentification({
        session_id: sessionId,
        id_type,
        value,
        password,
      }),
    ).unwrap();

    const idPay: IdPayload = { id_type, value, password };
    await localforage.setItem('signin.id_payload', idPay);
    await localforage.setItem('signin.step', 2);

    setIdPayload(idPay);
    setActiveStep(2);
  };

  const handleOTPSubmit = async () => {
    setInlineError(null);

    const values = await form.validateFields(['otp_code']);

    const otp_code =
      typeof values.otp_code === 'string'
        ? values.otp_code.trim()
        : String(values.otp_code ?? '');

    if (!otp_code) {
      setInlineError('Please enter the OTP code.');
      return;
    }

    let sessionId = step1Data?.session_id ?? null;
    if (!sessionId) {
      const cached = await localforage.getItem<Step1Data>('signin.session');
      sessionId = cached?.session_id ?? null;
    }
    if (!sessionId) {
      setInlineError('Session expired or missing. Please restart sign in.');
      return;
    }

    let finalIdPayload = idPayload;
    if (!finalIdPayload) {
      finalIdPayload =
        (await localforage.getItem<IdPayload>('signin.id_payload')) || null;
    }
    if (!finalIdPayload) {
      setInlineError('Missing identification details. Please redo Step 2.');
      setActiveStep(1);
      return;
    }

    const res = await dispatch(
      postSignInConfirmation({
        session_id: sessionId,
        id_type: finalIdPayload.id_type,
        value: finalIdPayload.value,
        password: finalIdPayload.password,
        otp_code,
      }),
    ).unwrap();

    try {
      await localforage.setItem('profile.user', res.user);
    } catch {
      /* ignore */
    }

    await Promise.all([
      localforage.removeItem('signin.session'),
      localforage.removeItem('signin.step'),
      localforage.removeItem('signin.id_payload'),
    ]);

    setRedirecting(true);
    await delay(50);

    const dest = pickNextDestination();
    clearStashedNext();
    router.push(dest);
  };

  /* ─── OTP resend ────────────────────────────────────────────────────────────
     No dedicated resend endpoint. We replay postSignInIdentification with the
     stored idPayload — the server re-triggers OTP delivery as a side effect.
     Falls back to step 1 if session or identification data is gone.
  ──────────────────────────────────────────────────────────────────────────── */

  const handleResendOtp = async () => {
    setInlineError(null);

    let sessionId = step1Data?.session_id ?? null;
    if (!sessionId) {
      const cached = await localforage.getItem<Step1Data>('signin.session');
      sessionId = cached?.session_id ?? null;
    }

    let payload = idPayload;
    if (!payload) {
      payload =
        (await localforage.getItem<IdPayload>('signin.id_payload')) || null;
    }

    // If either piece is missing we can't replay — drop back to the furthest
    // recoverable step rather than showing a confusing error
    if (!sessionId) {
      setActiveStep(0);
      return;
    }
    if (!payload) {
      setActiveStep(1);
      return;
    }

    // Note: isSubmitting lock is NOT set here — the OTP component owns its own
    // resending state. Setting isSubmitting would disable the Sign In button
    // and confuse the loading indicator during a resend.
    try {
      await dispatch(
        postSignInIdentification({
          session_id: sessionId,
          id_type: payload.id_type,
          value: payload.value,
          password: payload.password,
        }),
      ).unwrap();
      // OTP component clears its own digit state + restarts timer after this resolves
    } catch (err: unknown) {
      setInlineError(
        getProfessionalErrorMessage(
          extractErrorMessage(err, 'Failed to resend code. Please go back and try again.'),
        ),
      );
    }
  };

  /* ─── Primary submit dispatcher ─────────────────────────────────────────────
     validateFields throws (ValidateErrorEntity) on invalid input — that bubbles
     up to the finally and releases the lock, while the form renders its own
     inline field errors.
  ──────────────────────────────────────────────────────────────────────────── */

  const handleNext = async () => {
    if (isSubmitting || redirecting) return;

    setIsSubmitting(true);
    try {
      if (activeStep === 0) await handleIntroNext();
      if (activeStep === 1) await handleIdentificationNext();
      if (activeStep === 2) await handleOTPSubmit();
    } catch (err: any) {
      // validateFields rejections are handled by the form itself; other errors surface here
      setInlineError(
        getProfessionalErrorMessage(
          extractErrorMessage(err, 'An unexpected error occurred. Please try again.'),
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrev = () => {
    if (activeStep > 0) {
      setInlineError(null);
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleCancel = async () => {
    try {
      await Promise.all([
        localforage.removeItem('signin.session'),
        localforage.removeItem('signin.step'),
        localforage.removeItem('signin.id_payload'),
      ]);
    } catch {
      /* ignore */
    }
    form.resetFields();
    setInlineError(null);
    setStep1Data(null);
    setIdPayload(null);
    setActiveStep(0);
  };

  return (
    <div className="relative w-full flex min-h-screen items-stretch justify-center bg-white px-4 md:px-8">
      <div
        className={`flex w-full max-w-[1200px] flex-col bg-white md:flex-row ${
          redirecting ? 'pointer-events-none opacity-60' : 'opacity-100'
        } transition-opacity`}
      >
        {/* Left panel — form */}
        <div className="relative flex flex-1 flex-col items-center overflow-hidden bg-white px-6 py-8 md:px-12 md:py-12 shadow">
          <GridBackground />

          <div className="relative z-10 flex w-full max-w-[480px] flex-1 flex-col justify-center gap-6">
            {/* Logo */}
            <div className="flex justify-center items-center gap-0.5">
              <Logo
                href="/"
                width={402}
                height={124}
                className="w-[240px]"
                imgClassName="max-h-[80px]"
              />
            </div>

            {/* Headings */}
            <div className="text-left">
              <div className="mb-12 text-center">
                <h1 className="text-2xl sm:text-3xl font-medium mb-2 text-gray-900">
                  {signinCopy.welcome}
                </h1>
                <p className="max-w-full text-center mx-auto text-lg leading-relaxed text-gray-600">
                  {signinCopy.tagline}
                </p>
              </div>

              <p className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">
                Step {activeStep + 1} of 3
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {currentMeta.description}
              </p>
            </div>

            {/* Form */}
            <div className="mt-2">
              {inlineError && (
                <Alert
                  type="error"
                  message={
                    <div>
                      <div>{inlineError}</div>
                      {inlineError.includes('unavailable') && (
                        <div className="mt-2 text-xs">
                          Need help? Contact{' '}
                          <a
                            href="mailto:support@nyandaruapay.gov.ke"
                            className="underline text-blue-600 hover:text-blue-800"
                          >
                            support@nyandaruapay.gov.ke
                          </a>
                        </div>
                      )}
                    </div>
                  }
                  showIcon
                  className="mb-3 text-xs"
                />
              )}

              <Form
                form={form}
                layout="vertical"
                onFinish={handleNext}
                autoComplete="off"
                className="space-y-0"
              >
                {activeStep === 0 && <Introduction inlineError={inlineError} />}
                {activeStep === 1 && (
                  <Identification
                    form={form}
                    sessionId={step1Data?.session_id || undefined}
                    error={inlineError}
                  />
                )}
                {activeStep === 2 && (
                  // onResend is passed down; OTP component owns the timer,
                  // resend gate, digit clear, and timer restart internally.
                  // The manual resend <div> that was here has been removed.
                  <OTP
                    form={form}
                    errorProp={inlineError}
                    onResend={handleResendOtp}
                    durationSec={90}
                  />
                )}

                {/* Actions */}
                <div className="mt-6 flex flex-col gap-3">
                  <ThemedButton
                    htmlType="submit"
                    theme="primary"
                    variant="solid"
                    text={
                      isSubmitting
                        ? 'Please wait...'
                        : activeStep < 2
                          ? 'Continue'
                          : 'Sign In'
                    }
                    loading={isSubmitting}
                    disabled={isSubmitting || redirecting}
                    className="!h-11 !w-full !rounded-md !text-[15px] !font-semibold"
                  />

                  <div className="flex items-center justify-between gap-3">
                    <ThemedButton
                      theme="accent"
                      variant="outline"
                      text={activeStep === 0 ? 'Cancel' : 'Back'}
                      iconPosition="prefix"
                      icon={<FiArrowLeft />}
                      onClick={activeStep === 0 ? handleCancel : handlePrev}
                      disabled={isSubmitting || redirecting}
                      className="!h-10 !rounded-md !text-xs !font-semibold"
                    />
                  </div>

                  <div className="mt-0 flex flex-row justify-between items-center w-full text-sm">
                    <Link
                      href="/account/signup"
                      className="group flex items-center"
                    >
                      <span className="group-hover:underline transition-all !text-gray-800">
                        Don&apos;t have an account?{' '}
                      </span>
                      <span className="font-semibold ml-1 !text-[var(--color-accent)]">
                        Sign Up
                      </span>
                      <span className="ml-1 transition-transform translate-x-0 opacity-0 group-hover:translate-x-1 group-hover:opacity-100 !text-[var(--color-accent)]">
                        &rarr;
                      </span>
                    </Link>

                    <span className="text-gray-400 m-0">or</span>

                    <Link
                      href="/account/reset-password/"
                      className="text-(--color-primary)! group flex items-center"
                    >
                      <span className="font-semibold group-hover:underline transition-all">
                        Forgot Password?
                      </span>
                      <span className="ml-1 transition-transform translate-x-0 opacity-0 group-hover:translate-x-1 group-hover:opacity-100">
                        &rarr;
                      </span>
                    </Link>
                  </div>
                </div>
              </Form>
            </div>
          </div>

          <div className="relative z-10 mt-6 flex w-full justify-start text-xs text-gray-400 md:mt-10">
            <AuthFooter />
          </div>
        </div>

        {/* Right panel — hero image, desktop only */}
        <div className="hidden flex-shrink-0 items-stretch justify-center md:flex">
          <div className="relative h-[100vh] w-140 max-w-full">
            <Image
              src={signinCopy.heroImage}
              alt="Sign in hero"
              fill
              className="object-cover object-top"
              priority
            />
          </div>
        </div>
      </div>

      {/* Redirect overlay — shown while router.push is in flight */}
      {redirecting && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm">
          <div className="rounded-2xl bg-white p-6 shadow-xl">
            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
            <p className="text-center text-sm font-medium">
              Taking you to your page…
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SigninPage;
